import Amplify, { API, Auth, Cache, Storage } from 'aws-amplify'
import awsconfig from '@/aws-exports.js'

Amplify.configure(awsconfig)
Storage.configure(awsconfig.Storage.common)



export default {
  methods: {
    /**
     * GET APIを呼び出す
     * @param {string} apiName API名
     * @param {string} path APIのパス
     * @param {Dict} data 送信データ
     * @param {Function} errorCallback エラー時の処理
     * @return APIの戻り値
     */
    async getData (apiName, path, data = {}, errorCallback = null) {
      // IDトークン更新
      const idToken = await this.refreshIdToken()
      if (!idToken || !this.checkLoginTime()) {
        return null // エラー：IDトークンが更新できない、またはサービス時間外
      }

      const opt = {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': idToken,
        },
        response: true,
        queryStringParameters: data
      }

      const response = await API.get(apiName, path, opt).catch(e => this.apiError(e, errorCallback))
      return response.data.result
    },
    /**
     * POST APIを呼び出す
     * @param {string} apiName API名
     * @param {string} path APIのパス
     * @param {Dict} data 送信データ
     * @param {Function} errorCallback エラー時の処理
     * @return APIの戻り値
     */
    async postData (apiName, path, data, errorCallback = null) {
      // IDトークン更新
      const idToken = await this.refreshIdToken()
      if (!idToken || !this.checkLoginTime()) {
        return null // エラー：IDトークンが更新できない、またはサービス時間外
      }

      const opt = {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': idToken,
        },
        response: true,
        body: data
      }

      const response = await API.post(apiName, path, opt).catch(e => this.apiError(e, errorCallback))
      return response.data.result
    },
    /**
     * PUT APIを呼び出す
     * @param {string} apiName API名
     * @param {string} path APIのパス
     * @param {Dict} data 送信データ
     * @param {Function} errorCallback エラー時の処理
     * @return APIの戻り値
     */
    async putData (apiName, path, data, errorCallback = null) {
      // IDトークン更新
      const idToken = await this.refreshIdToken()
      if (!idToken || !this.checkLoginTime()) {
        return null // エラー：IDトークンが更新できない、またはサービス時間外
      }

      const opt = {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': idToken,
        },
        response: true,
        body: data
      }

      const response = await API.put(apiName, path, opt).catch(e => this.apiError(e, errorCallback))
      return response.data.result
    },
    /**
     * DELETE APIを呼び出す
     * @param {string} apiName API名
     * @param {string} path APIのパス
     * @param {Dict} data 送信データ
     * @param {Function} errorCallback エラー時の処理
     * @return APIの戻り値
     */
    async deleteData (apiName, path, data = {}, errorCallback = null) {
      // IDトークン更新
      const idToken = await this.refreshIdToken()
      if (!idToken || !this.checkLoginTime()) {
        return null // エラー：IDトークンが更新できない、またはサービス時間外
      }

      const opt = {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': idToken,
        },
        response: true,
        body: data
      }

      const response = await API.del(apiName, path, opt).catch(e => this.apiError(e, errorCallback))
      return response.data.result
    },
    /**
     * 複数APIを非同期で呼び出す
     * @param {Object} requests 呼ぶAPIと呼び出し後の関数の配列
     * @param {Function} errorCallback エラー時の処理
     * @return APIの戻り値
     */
    async multiRequest (requests, errorCallback = null) {
      // IDトークン更新
      const idToken = await this.refreshIdToken()
      if (!idToken || !this.checkLoginTime()) {
        return null // エラー：IDトークンが更新できない、またはサービス時間外
      }

      const processes = []

      requests.forEach(request => {
        processes.push(request.process)
      })

      const results = await Promise.all(processes).catch(e => this.apiError(e, errorCallback))
      for (let i = 0; i < requests.length; i++) {
        requests[i].callback(results[i])
      }
    },
    /**
     * API呼び出し後のエラーを処理する
     * @param {Object} e エラー
     * @param {Function} errorCallback エラー時の処理
     */
    apiError (e, errorCallback = null) {
      console.log(e)
      this.stopLoading()

      if ((e.toString() === 'Error: Network Error') || (e.response && e.response.status === 401)) {
        this.$store.commit('error/setNetworkErrorModalShow', true)
      } else if (e.response && e.response.status === 403) {
        this.$store.commit('error/setAuthErrorModalShow', true)
      } else {
        const error = e.response.data.error
        error.status = e.response.status
        this.$store.commit('error/setCommonErrorModalShow', error)
      }

      if (errorCallback) {
        errorCallback()
      }
    },
    /**
     * ファイルをS3からダウンロードする
     * @param {String} key ファイルパス
     */
     async downloadFile (key) {
      // IDトークン更新
      const idToken = await this.refreshIdToken()
      if (!idToken || !this.checkLoginTime()) {
        return false // エラー：IDトークンが更新できない、またはサービス時間外
      }

      Storage.get(key)
        .then(result => {
          const link = document.createElement('a')
          link.href = result
          // link.download = `log.log`
          link.click()

          this.stopLoading()
          return true
        })
        .catch(err => {
          console.log(err)
          this.stopLoading()
          return false
        })
    },
    /**
     * ファイルをS3にアップロードする
     * @param {String} key ファイルパス
     * @param {File} file ファイル
     * @param {Function} callback アップロード完了後の処理
     */
     async uploadFile (key, file, callback = null) {
      // IDトークン更新
      const idToken = await this.refreshIdToken()
      if (!idToken || !this.checkLoginTime()) {
        return false // エラー：IDトークンが更新できない、またはサービス時間外
      }

      Storage.put(key, file, {
        metadata: {
          Authorization: idToken,
        }
      }).then(() => {
        if (callback) {
          callback()
        }
        return true
      }).catch(err => {
          console.log(err)
          alert(err)
          this.stopLoading()
          return false
        })
    },
    /**
     * ログアウト
     */
    signOut () {
      Auth.signOut()
        .then(() => {
          this.$store.commit('header/setUser', null)
          this.$store.commit('auth/logout')
          Cache.clear()
          if (this.$store.state.appName === 'common') {
            this.$router.push({ path: '/login' })
          } else {
            window.location = `${process.env.VUE_APP_COMMON_APP_URL}/login`
          }
        })
        .catch(err => {
          console.log('err', err)
        })
    },
    /**
     * IDトークンリフレッシュ
     */
    async refreshIdToken () {
      try {
        const data = await Auth.currentAuthenticatedUser()
        if (data && data.signInUserSession) {
          await this.$store.commit('auth/login', data)
          return data.signInUserSession.idToken.jwtToken
        } else {
          this.stopLoading()
          this.$store.commit('error/setAuthErrorModalShow', true)
          return null
        }
      } catch {() => {
        this.stopLoading()
        this.$store.commit('error/setAuthErrorModalShow', true)
        return null
      }}
    },
  }
}
